import { LitElement, html } from 'lit-element';
import IMask from 'imask';

import { decimalSeparator, thousandSeparator } from '../../utils/intl';

import dispatchCustomEvent from '../../utils/dispatch-custom-event';
import translate from '../../utils/webcomponents/translate';

import { types as alertTypes, priceTypes, thresholdDirections as directions } from './alert-types';
import alertEvents from './alert-events';

import '../loader';
import '../message';
import '../icon';

import * as alertApi from './alert-api';

const isSelected = (modelValue, optionValue) => modelValue === optionValue;

const formTemplate = (id, priceType, threshold, direction) => {
	const labelPriceType = translate('alerts', 'target', 'Alert target');
	const labelDirection = translate('alerts', 'direction', 'Direction');

	const labelBid = translate('alerts', priceTypes.bid.resourceKey, 'Bid');
	const labelAsk = translate('alerts', priceTypes.ask.resourceKey, 'Ask');

	const labelUp = translate('alerts', directions.up.resourceKey, 'Rises above');
	const labelDown = translate('alerts', directions.down.resourceKey, 'Drops below');

	const labelValue = translate('alerts', 'value', 'Value');
	const labelCancel = translate('alerts', 'cancel', 'Cancel');


	const labelSubmit = id
		? translate('alerts', 'saveChanges', 'Save changes')
		: translate('alerts', 'setAlert', 'Set alert');

	return html`
		<form class="form trailer-reset">
			<div class="field">
				<label class="field__label" for="priceType">
					${labelPriceType}
				</label>

				<select id="priceType" name="priceType" class="field__input" required>
					<option value="${priceTypes.bid.value}" ?selected=${isSelected(priceType, priceTypes.bid.value)}>${labelBid}</option>
					<option value="${priceTypes.ask.value}" ?selected=${isSelected(priceType, priceTypes.ask.value)}>${labelAsk}</option>
				</select>
			</div>

			<div class="field">
				<label class="field__label" for="thresholdDirection">
					${labelDirection}
				</label>

				<select id="thresholdDirection" name="thresholdDirection" class="field__input" required>
					<option value="${directions.down.value}" ?selected=${isSelected(direction, directions.down.value)}>${labelDown}</option>
					<option value="${directions.up.value}" ?selected=${isSelected(direction, directions.up.value)}>${labelUp}</option>
				</select>
			</div>

			<div class="field">
				<label class="field__label" for="value">
					${labelValue}
				</label>
				<input type="text" class="field__input" name="value" id="value" value="${threshold}" autocomplete="off" required />
			</div>

			<div class="form__footer form__footer--centered">
				<button type="submit" class="button">${labelSubmit}</button>
				<button type="button" class="button button--ghost js-close">${labelCancel}</button>
			</div>

			<input type="hidden" name="type" id="type" value="${alertTypes.THRESHOLD}">
		</form>
	`;
};

const succesTemplate = (isUpdate) => {
	const message = isUpdate
		? translate('alerts', 'changesSaved', 'The changes are saved')
		: translate('alerts', 'alertSet', 'The alert is set');

	const labelClose = translate('alerts', 'close', 'Close');

	return html`
		<x-message modifier="positive" icon="checkmark" message="${message}"></x-message>

		<div class="form__footer form__footer--centered">
			<button type="button" class="button js-close">${labelClose}</button>
		</div>
	`;
};

class AlertEditForm extends LitElement {
	static get properties() {
		return {
			isError: { type: Boolean },
			isSuccess: { type: Boolean },
			isLoading: { type: Boolean },

			options: { type: Object },
		};
	}

	get isUpdate() {
		const { alertConditionId: id } = this.options;

		return id !== null && typeof id !== 'undefined';
	}

	get title() {
		const { triggered } = this.options;

		if (this.isUpdate) {
			return translate('alerts', 'editTitle', 'Edit alert');
		}

		if (triggered) {
			return translate('alerts', 'reactivateTitle', 'Reactivate alert');
		}

		return translate('alerts', 'addTitle', 'Add alert');
	}

	constructor() {
		super();

		this.isError = false;
		this.isSuccess = false;
		this.isLoading = false;

		this.portfolioItemId = null;

		this.onFormSubmit = this.onSubmit.bind(this);
	}

	disconnectedCallback() {
		this.valueFormatted.destroy();

		this.removeEventListener('submit', this.onSubmit);

		super.disconnectedCallback();
	}

	async firstUpdated() {
		super.firstUpdated();

		this.addEventListener('submit', this.onSubmit);

		this.createFormattedInputs();
	}

	updated() {
		dispatchCustomEvent(this, 'updated');
	}

	render() {
		const errorFeedback = translate('alerts', 'somethingWentWrong', 'Something went wrong. Please try again.');

		return html`
			<x-loader .isLoading="${this.isLoading}"></x-loader>

			${this.isError ? html`<x-message modifier="error" icon="warning" message="${errorFeedback}"></x-message>` : ''}

			${this.isSuccess ? this.renderSucces() : this.renderForm()}
		`;
	}

	renderSucces() {
		return succesTemplate(this.isUpdate);
	}

	renderForm() {
		const {
			alertConditionId,
			priceType,
			threshold,
			thresholdDirection,
		} = this.options;

		return formTemplate(alertConditionId, priceType, threshold, thresholdDirection);
	}

	createFormattedInputs() {
		this.valueFormatted = new IMask(this.querySelector('#value'), {
			mask: Number,
			scale: 3,
			thousandsSeparator: thousandSeparator,
			radix: decimalSeparator,
			padFractionalZeros: true,
			signed: false,
			min: 0.01,
		});
	}

	onSubmit(e) {
		e.preventDefault();

		this.updateAlert(e.target);
	}

	onSuccess() {
		this.isSuccess = true;

		dispatchCustomEvent(document.body, alertEvents.CHANGED);
	}

	async updateAlert(form) {
		const threshold = this.valueFormatted.unmaskedValue;
		const {
			type: { value: type },
			priceType: { value: priceType },
			thresholdDirection: { value: thresholdDirection },
		} = form;

		const data = JSON.stringify({
			alertConditionId: this.options.alertConditionId,
			portfolioItemId: this.options.portfolioItemId,
			isin: this.options.isin,
			threshold,
			type,
			priceType,
			thresholdDirection,
		});

		this.isLoading = true;
		this.isError = false;

		try {
			if (this.isUpdate) {
				await alertApi.update(data);
			} else {
				await alertApi.add(data);
			}

			this.onSuccess();
		} catch (e) {
			this.isError = true;
		} finally {
			this.isLoading = false;
		}
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-alert-edit-form', AlertEditForm);

export default AlertEditForm;
